import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../../GlobalContext";

import "../../common/Util.css";
import "../../common/skeleton-effect.css";
import { Table, Button, Form, Dropdown } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { FiSearch, FiPlus, FiDownload, FiMoreVertical, FiUser } from "react-icons/fi";
import { format } from "date-fns";
import { getVendorListApi, deleteVendorApi, downloadVendorReportApi, downloadBase64File } from "./vendor-api";
import LoaderModal from "../../common/loader";
import DeleteModal from "../../common/deleteModel";
import DateRangePicker from "../../common/dateRange";
import CommonSnackbar from "../../common/reusable-commponent";
import { sanitizeInput } from "../../common/security-utils";

// SVGS
import { ReactComponent as RarIc } from "../../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../../assets/svg/lar_ic.svg";
import { ReactComponent as EmailView } from "../../../assets/svg/email_view_ic.svg";
import { ReactComponent as ListView } from "../../../assets/svg/list_view_ic.svg";
import { ReactComponent as SelectGrid } from "../../../assets/svg/seelct_grid_ic.svg";
import { ReactComponent as SelectList } from "../../../assets/svg/select_list_ic.svg";
import { ReactComponent as DownArrowIc } from "../../../assets/svg/down_arrow_ics.svg";
import editIcon from "../../../assets/svg/edit_ic.svg";
import deleteIcon from "../../../assets/svg/delete_ic.svg";

const VendorList = () => {
  const { rolePermissionList, userType } = useGlobal();
  function checkPermission(permission) {
    return rolePermissionList.findIndex((element) => element["name"] === "Vendor" && element["menu_permission"] === true && element[permission] === true) > -1 ? true : false;
  }
  const navigate = useNavigate();
  const iconStyle = { width: "16px", height: "16px" };
  const [selectedSortby, setSelectedSortBy] = useState({
    value: "created desc",
    label: "Created (Newest)",
  });
  const [active, setActive] = useState("list");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  const [search, setSearch] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [statusSelect, setStatusSelect] = useState(null);
  const [startDateSave, setStartDateSave] = useState(null);
  const [endDateSave, setEndDateSave] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleDateChange = ({ start, end }) => {
    setStartDate(start);
    setEndDate(end);
    const formattedStart = start ? format(start, "yyyy-MM-dd") : null;
    const formattedEnd = end ? format(end, "yyyy-MM-dd") : null;
    setStartDateSave(formattedStart);
    setEndDateSave(formattedEnd);
  };

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  useEffect(() => {
    getVendorList();
  }, [itemsPerPage, itemOffset, search, selectedSortby, statusSelect, startDateSave, endDateSave]);

  const getVendorList = async () => {
    setIsLoading(true);
    const [field, dir] = selectedSortby?.value?.split(" ") || ["created", "desc"];
    let sortField = "vendors.created_at";
    if (field === "name") {
      sortField = "vendors.vendor_name";
    }

    const param = {
      limit: itemsPerPage,
      skip: itemOffset * itemsPerPage,
      search: sanitizeInput(search),
      status: statusSelect?.value ?? "",
      from_date: startDateSave,
      to_date: endDateSave,
      sortByField: sortField,
      sortBy: dir,
    };
    try {
      const response = await getVendorListApi(param);
      if (response.data && response.data.success === true) {
        setTotalCount(response.data.total_recs ?? 0);
        setPageCount(response.data.total_pgs ?? 0);
        setVendorList(response.data.vendors ?? []);
      }
    } catch {
      triggerSnackbar("Error fetching vendors", false);
    } finally {
      setIsLoading(false);
    }
  };

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const deleteVendor = async () => {
    try {
      const response = await deleteVendorApi(deleteId);
      if (response.data && response.data.success === true) {
        getVendorList();
        setDeleteId(null);
        setIsOpenDelete(false);
        triggerSnackbar(response.data.message || "Vendor deleted successfully", true);
      } else {
        triggerSnackbar(response.data?.message || "Failed to delete vendor", false);
      }
    } catch {
      triggerSnackbar("An error occurred while deleting vendor", false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setIsLoading(true);
    try {
      const [field, dir] = selectedSortby?.value?.split(" ") || ["created", "desc"];
      let sortField = "order_vendors.created_at";
      if (field === "name") {
        sortField = "order_vendors.vendor_name";
      }

      const param = {
        limit: itemsPerPage,
        skip: itemOffset * itemsPerPage,
        search: search,
        status: statusSelect?.value ?? "",
        from_date: startDateSave,
        to_date: endDateSave,
        sortByField: sortField,
        sortBy: dir,
      };

      const response = await downloadVendorReportApi(param);
      if (response && response.data && response.data.success) {
        if (response.data.base64) {
          downloadBase64File(response.data.base64, response.data.file_name || "vendor_report.xlsx");
          triggerSnackbar("Report downloaded successfully!", true);
        } else {
          triggerSnackbar(response.data.message || "Report generated successfully!", true);
        }
      } else {
        triggerSnackbar(response?.data?.message || "Failed to generate report", false);
      }
    } catch {
      triggerSnackbar("An error occurred while downloading the report.", false);
    } finally {
      setIsLoading(false);
    }
  };

  const sortByOption = [
    { value: "created asc", label: "Created (Oldest)" },
    { value: "created desc", label: "Created (Newest)" },
    { value: "name asc", label: "Name (A - Z)" },
    { value: "name desc", label: "Name (Z - A)" },
  ];

  return (
    <>
      <DeleteModal
        title={"vendor"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteVendor}
      />
      <div style={{ backgroundColor: "#F5F7FA", minHeight: "100vh" }}>
        <div className="p-0 bg-half-white">
          <div className="bg-half-white rounded mt-2">
            {/* Header Toolbar */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div className="d-flex align-items-center gap-3">
                <h5 className="fs-14 fw-600 text-secondary m-0" style={{ color: "#9299A7" }}>
                  VENDORS : {totalCount}
                </h5>

                <div className="d-flex align-items-center gap-2 ">
                  <Dropdown>
                    <Dropdown.Toggle variant="light" size="sm" className="fs-13 fw-500 py-1 px-3 d-flex align-items-center justify-content-between bg-white border  rounded" style={{ color: "#495057" }}>
                      Status : {statusSelect?.label || "All"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setStatusSelect(null)}>All</Dropdown.Item>
                      <Dropdown.Item onClick={() => setStatusSelect({ value: true, label: "Active" })}>Active</Dropdown.Item>
                      <Dropdown.Item onClick={() => setStatusSelect({ value: false, label: "Inactive" })}>Inactive</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <div
                    className="d-flex align-items-center px-1"
                    style={{
                      borderRadius: "8px",
                      maxWidth: "200px",
                      height: "32px",
                      border: "1px solid #EBEBEB",
                      backgroundColor: "white",
                    }}
                  >
                    <span className="fs-12 fw-400 textGrey">Date :</span>
                    <div className="dropdown-text1 ms-2" style={{ marginBottom: "4px" }}>
                      <DateRangePicker onChange={handleDateChange} startDate={startDate} endDate={endDate} style={{ border: "none", padding: "0", backgroundColor: "transparent" }} />
                    </div>
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="light"
                        id="dropdown-sort"
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          borderRadius: "8px",
                          border: "1px solid #EBEBEB",
                          padding: "6px 12px",
                          backgroundColor: "white",
                        }}
                        title={selectedSortby.label}
                      >
                        <span className="fs-12 fw-400 textGrey">Sort by :</span>
                        <span className="d-flex align-items-center px-1">
                          <span className="dropdown-text">{selectedSortby.label}</span>
                        </span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        style={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #EBEBEB",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {sortByOption.map((option) => (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            key={option.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSortBy(option);
                            }}
                            active={selectedSortby === option.value}
                          >
                            {option.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <span
                    className="text-muted text-decoration-underline fs-14 fw-400"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setStatusSelect(null);
                      setSearch("");
                      setStartDate(null);
                      setEndDate(null);
                      setStartDateSave(null);
                      setEndDateSave(null);
                      setSelectedSortBy({
                        value: "created desc",
                        label: "Created (Newest)",
                      });
                    }}
                  >
                    Reset Filters
                  </span>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="position-relative">
                  <FiSearch
                    className="position-absolute text-muted"
                    onClick={() => setIsSearchFocused(true)}
                    style={{
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      cursor: "pointer",
                    }}
                  />
                  <Form.Control
                    type="text"
                    placeholder=""
                    maxLength={100}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border bg-white"
                    style={{
                      height: "32px",
                      paddingLeft: "30px",
                      borderRadius: "20px",
                      width: isSearchFocused ? "140px" : "40px",
                      transition: "width 0.3s",
                      cursor: isSearchFocused ? "text" : "pointer",
                    }}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                  />
                </div>

                {checkPermission("create_permission") && (
                  <div
                    className=" fs-13 fw-700 d-flex align-items-center gap-2 rounded"
                    style={{
                      padding: "5px 12px",
                      backgroundColor: "#3469E7",
                      borderColor: "#3469E7",
                      color: "#FFFFFF",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/vendor/create")}
                  >
                    <FiPlus /> Add Vendor
                  </div>
                )}

                {checkPermission("create_permission") && (
                  <div
                    variant="outline-primary"
                    className="fs-13 fw-600 py-1 px-3 d-flex align-items-center gap-2 rounded"
                    style={{
                      color: "#3b82f6",
                      border: "1px solid #3469E7",
                      cursor: "pointer",
                    }}
                    onClick={handleDownloadReport}
                  >
                    <FiDownload /> Download Report
                  </div>
                )}

                <div
                  className="rounded-3 toggleBg d-flex"
                  style={{
                    width: "80px",
                    height: "32px",
                    backgroundColor: "#FFFFFF",
                    padding: "4px",
                    gap: "4px",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #EBEBEB",
                  }}
                >
                  <div
                    onClick={() => setActive("list")}
                    className={`d-flex align-items-center justify-content-center rounded-3 ${active === "list" ? "bg-primary1" : ""}`}
                    style={{
                      width: "36px",
                      height: "24px",
                      cursor: "pointer",
                    }}
                  >
                    {active === "list" ? <ListView style={iconStyle} /> : <SelectList style={iconStyle} />}
                  </div>

                  <div
                    onClick={() => setActive("grid")}
                    className={`d-flex align-items-center justify-content-center rounded-3 ${active === "grid" ? "bg-primary1" : ""}`}
                    style={{
                      width: "36px",
                      height: "24px",
                      cursor: "pointer",
                    }}
                  >
                    {active === "grid" ? <SelectGrid style={iconStyle} /> : <EmailView style={iconStyle} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            {active === "list" ? (
              <div className="table-responsive border rounded" style={{ borderColor: "#e9ecef" }}>
                <Table className="w-100 m-0" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr className="bg-white">
                      {/* <th
                          className="py-2 px-3 border-bottom border-end"
                          style={{ width: "50px", borderColor: "#e9ecef" }}
                        >
                          <Form.Check type="checkbox" />
                        </th> */}
                      <th
                        className="py-2 px-3 border-bottom border-end text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">VENDOR ID</div>
                      </th>
                      <th
                        className="py-2 px-3 border-bottom border-end text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">VENDOR NAME</div>
                      </th>
                      <th
                        className="py-2 px-3 border-bottom border-end text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">TYPE</div>
                      </th>
                      <th
                        className="py-2 px-3 border-bottom border-end text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">MOBILE NUMBER</div>
                      </th>
                      <th
                        className="py-2 px-3 border-bottom border-end text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">EMAIL</div>
                      </th>
                      <th
                        className="py-2 px-3 border-bottom text-secondary text-uppercase"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          borderColor: "#e9ecef",
                          color: "#9299A7",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">STATUS</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="fw-400" style={{ color: "#28303F" }}>
                    {isLoading
                      ? Array.from({ length: itemsPerPage }).map((_, index) => (
                        <tr key={index}>
                          <td className="py-2 px-3 border-bottom border-end" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton skeleton-text" style={{ height: "16px", width: "80%" }} />
                          </td>
                          <td className="py-2 px-3 border-bottom border-end" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton skeleton-text" style={{ height: "16px", width: "70%" }} />
                          </td>
                          <td className="py-2 px-3 border-bottom border-end" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton skeleton-text" style={{ height: "16px", width: "60%" }} />
                          </td>
                          <td className="py-2 px-3 border-bottom border-end" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton skeleton-text" style={{ height: "16px", width: "75%" }} />
                          </td>
                          <td className="py-2 px-3 border-bottom border-end" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton skeleton-text" style={{ height: "16px", width: "85%" }} />
                          </td>
                          <td className="d-flex align-items-center justify-content-center px-3 py-2 border-bottom" style={{ borderColor: "#F1F2F4", verticalAlign: "middle" }}>
                            <div className="skeleton" style={{ height: "28px", width: "80px", borderRadius: "4px" }} />
                          </td>
                        </tr>
                      ))
                      : vendorList.map((vendor, index) => (
                        <tr
                          key={index}
                          onMouseEnter={() => setHoveredRowIndex(index)}
                          onMouseLeave={() => setHoveredRowIndex(null)}
                          onClick={() => {
                            if (checkPermission("view_permission")) {
                              navigate("/vendor/view", {
                                state: { id: vendor.id },
                              });
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <td
                            className="py-2 px-3 border-bottom border-end "
                            style={{
                              fontSize: "13px",
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                              color: "#28303F",
                            }}
                          >
                            {sanitizeInput(String(vendor.vendor_id || ""))}
                          </td>
                          <td
                            className="py-2 px-3 border-bottom border-end"
                            style={{
                              fontSize: "13px",
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                            }}
                          >
                            {sanitizeInput(String(vendor.vendor_name || ""))}
                          </td>
                          <td
                            className="py-2 px-3 border-bottom border-end "
                            style={{
                              fontSize: "13px",
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                            }}
                          >
                            {sanitizeInput(String(vendor.type || ""))}
                          </td>
                          <td
                            className="py-2 px-3 border-bottom border-end"
                            style={{
                              fontSize: "13px",
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                            }}
                          >
                            {sanitizeInput(String(vendor.mobilenumber || ""))}
                          </td>
                          <td
                            className="py-2 px-3 border-bottom border-end"
                            style={{
                              fontSize: "13px",
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                            }}
                          >
                            {sanitizeInput(String(vendor.email || ""))}
                          </td>
                          <td
                            className="d-flex align-items-center justify-content-center position-relative px-3 py-2 border-bottom"
                            style={{
                              borderColor: "#F1F2F4",
                              verticalAlign: "middle",
                            }}
                          >
                            <span
                              className="d-inline-block rounded"
                              style={{
                                backgroundColor: vendor.status ? "#D4F7D4" : "#FFE9D5",
                                color: vendor.status ? "#0F9D0F" : "#F57600",
                                padding: "6px 16px",
                                fontSize: "11px",
                                minWidth: "80px",
                              }}
                            >
                              {vendor.status ? "ACTIVE" : "INACTIVE"}
                            </span>
                            {hoveredRowIndex === index && (
                              <div style={{ position: "absolute", right: "35px" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "start",
                                    alignItems: "center",
                                  }}
                                >
                                  {checkPermission("edit_permission") && (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate("/vendor/create", {
                                          state: { id: vendor.id },
                                        });
                                      }}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "100px",
                                        background: "#FFFFFF",
                                        cursor: "pointer",
                                        border: "0.8px solid #E5E5E5",
                                      }}
                                    >
                                      <img src={editIcon} alt="edit" />
                                    </div>
                                  )}
                                  <span style={{ marginLeft: "16px" }}></span>
                                  {checkPermission("delete_permission") && (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteId(vendor.id);
                                      }}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "100px",
                                        background: "#FFFFFF",
                                        cursor: "pointer",
                                        border: "0.8px solid #E5E5E5",
                                      }}
                                    >
                                      <img src={deleteIcon} alt="delete" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3 text-start">
                {isLoading
                  ? Array.from({ length: itemsPerPage }).map((_, index) => (
                    <div key={index} className="bg-white rounded p-3 d-flex flex-wrap align-items-center justify-content-between shadow-sm" style={{ borderColor: "#EBEBEB" }}>
                      <div className="d-flex align-items-center gap-3" style={{ minWidth: "250px", flex: "1" }}>
                        <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
                        <div className="d-flex flex-column gap-1">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "150px", height: "14px" }} />
                          <div className="skeleton skeleton-text" style={{ width: "150px", height: "14px" }} />
                        </div>
                      </div>

                      <div className="d-none d-md-block" style={{ width: "1px", height: "40px", backgroundColor: "#EBEBEB", margin: "0 15px" }}></div>

                      <div className="d-flex flex-column gap-1" style={{ minWidth: "200px", flex: "1" }}>
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "120px" }} />
                        <div className="skeleton skeleton-text-sm" style={{ width: "130px" }} />
                      </div>

                      <div className="d-none d-md-block" style={{ width: "1px", height: "40px", backgroundColor: "#EBEBEB", margin: "0 15px" }}></div>

                      <div className="d-flex flex-column gap-1" style={{ minWidth: "250px", flex: "1" }}>
                        <div className="skeleton skeleton-text-sm" style={{ width: "180px" }} />
                      </div>

                      <div className="d-none d-md-block" style={{ width: "1px", height: "40px", backgroundColor: "#EBEBEB", margin: "0 15px" }}></div>

                      <div className="d-flex align-items-center gap-3 justify-content-end" style={{ minWidth: "150px" }}>
                        <div className="skeleton" style={{ height: "28px", width: "80px", borderRadius: "4px" }} />
                        <div className="skeleton" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
                      </div>
                    </div>
                  ))
                  : vendorList.map((vendor, index) => (
                    <div
                      key={index}
                      className="bg-white rounded p-3 d-flex flex-wrap align-items-center justify-content-between shadow-sm"
                      style={{ borderColor: "#EBEBEB", cursor: "pointer" }}
                      onClick={() => {
                        if (checkPermission("view_permission")) {
                          navigate("/vendor/view", {
                            state: { id: vendor.id },
                          });
                        }
                      }}
                    >
                      <div className="d-flex align-items-center gap-3" style={{ minWidth: "250px", flex: "1" }}>
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#F0F5FF",
                          }}
                        >
                          <FiUser size={18} color="#3469E7" />
                        </div>
                        <div>
                          <div className="fw-bold text-dark fs-14">{sanitizeInput(String(vendor.vendor_name || ""))}</div>
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <span className="text-muted fs-12">{sanitizeInput(String(vendor.vendor_id || ""))}</span>
                          </div>
                        </div>
                      </div>

                      <div
                        className="d-none d-md-block"
                        style={{
                          width: "1px",
                          height: "40px",
                          backgroundColor: "#EBEBEB",
                          margin: "0 15px",
                        }}
                      ></div>

                      <div className="d-flex flex-column gap-1" style={{ minWidth: "200px", flex: "1" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-secondary fs-11 fw-bold text-uppercase" style={{ minWidth: "60px", letterSpacing: "0.5px" }}>
                            TYPE :
                          </span>
                          <span className="fs-13 text-dark fw-500">{sanitizeInput(String(vendor.type || ""))}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-secondary fs-11 fw-bold text-uppercase" style={{ minWidth: "60px", letterSpacing: "0.5px" }}>
                            MOBILE :
                          </span>
                          <span className="fs-13 text-dark fw-500">{sanitizeInput(String(vendor.mobilenumber || ""))}</span>
                        </div>
                      </div>

                      <div
                        className="d-none d-md-block"
                        style={{
                          width: "1px",
                          height: "40px",
                          backgroundColor: "#EBEBEB",
                          margin: "0 15px",
                        }}
                      ></div>

                      <div className="d-flex flex-column gap-1" style={{ minWidth: "250px", flex: "1" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-secondary fs-11 fw-bold text-uppercase" style={{ minWidth: "50px", letterSpacing: "0.5px" }}>
                            EMAIL :
                          </span>
                          <span className="fs-13 text-dark fw-500">{sanitizeInput(String(vendor.email || ""))}</span>
                        </div>
                      </div>

                      <div
                        className="d-none d-md-block"
                        style={{
                          width: "1px",
                          height: "40px",
                          backgroundColor: "#EBEBEB",
                          margin: "0 15px",
                        }}
                      ></div>

                      <div className="d-flex align-items-center gap-3 justify-content-end" style={{ minWidth: "150px" }}>
                        <span
                          className="d-inline-block rounded"
                          style={{
                            backgroundColor: vendor.status ? "#D4F7D4" : "#FFE9D5",
                            color: vendor.status ? "#0F9D0F" : "#F57600",
                            padding: "6px 16px",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          {vendor.status ? "ACTIVE" : "INACTIVE"}
                        </span>
                        <div onClick={(e) => e.stopPropagation()}>
                          {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                            <Dropdown>
                              <Dropdown.Toggle variant="link" className="text-muted p-0 border-0 no-arrow d-flex align-items-center dropdown-toggle-no-caret" style={{ boxShadow: "none" }}>
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center border"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: "white",
                                  }}
                                >
                                  <FiMoreVertical size={16} />
                                </div>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end" className="shadow-sm border-0">
                                {checkPermission("edit_permission") && (
                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate("/vendor/create", {
                                        state: { id: vendor.id },
                                      })
                                    }
                                    className="fs-13"
                                  >
                                    Edit
                                  </Dropdown.Item>
                                )}
                                {checkPermission("delete_permission") && (
                                  <Dropdown.Item onClick={() => setDeleteId(vendor.id)} className="fs-13 text-danger">
                                    Delete
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Pagination Footer */}
            <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <Dropdown>
                <span className="pagination-color">Records per page: &nbsp;</span>
                <Dropdown.Toggle id="dropdown-basic" className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0">
                  <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                    {itemsPerPage} <DownArrowIc />
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="my-2" style={{ minWidth: "unset", width: "30%" }}>
                  {[10, 25, 50, 75, 100].map((size) => (
                    <Dropdown.Item
                      key={size}
                      onClick={() => {
                        setItemsPerPage(size);
                        setItemOffset(0);
                      }}
                      active={itemsPerPage === size}
                      className="text-center"
                    >
                      {size}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <div className="d-flex align-items-center gap-2">
                <ReactPaginate
                  previousLabel={
                    <div
                      className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                      style={{
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <LarIc />
                    </div>
                  }
                  nextLabel={
                    <div
                      className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                      style={{
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <RarIc />
                    </div>
                  }
                  breakLabel={"..."}
                  pageCount={pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
      <LoaderModal show={isLoading} />
    </>
  );
};

export default VendorList;
